import type { Account } from "@/entities/account/model/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { mapAccountToUI, mapAccountToUpdate } from "../account-mappers";
import { AccountOutputSchema, accountsApi, GetAccountsParams } from "../accounts";

export const accountsKeys = {
  all: ["accounts"] as const,
  lists: () => [...accountsKeys.all, "list"] as const,
  list: (filters?: GetAccountsParams) => [...accountsKeys.lists(), filters] as const,
  details: () => [...accountsKeys.all, "detail"] as const,
  detail: (id: string) => [...accountsKeys.details(), id] as const,
  applications: (id: string) => [...accountsKeys.detail(id), "applications"] as const,
};

export function useAccounts(params?: GetAccountsParams) {
  return useQuery({
    queryKey: accountsKeys.list(params),
    queryFn: async () => {
      const response = await accountsApi.getAccounts(params);
      return {
        items: response.items.map(mapAccountToUI),
        total: response.total,
        offset: response.offset,
        limit: response.limit,
      };
    },
  });
}

export function useAccount(accountId: string) {
  return useQuery({
    queryKey: accountsKeys.detail(accountId),
    queryFn: async () => {
      const data = await accountsApi.getAccount(accountId);
      return mapAccountToUI(data);
    },
    enabled: !!accountId,
  });
}

export function useAccountApplications(accountId: string) {
  return useQuery({
    queryKey: accountsKeys.applications(accountId),
    queryFn: () => accountsApi.getAccountApplications(accountId),
    enabled: !!accountId,
  });
}

export function useCreateAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Parameters<typeof accountsApi.createAccount>[0]) => {
      const apiData = await accountsApi.createAccount(data);
      return mapAccountToUI(apiData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountsKeys.lists() });
    },
  });
}

export function useUpdateAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      accountId,
      account,
    }: {
      accountId: string;
      account: Partial<Account>;
    }) => {
      const updateData = mapAccountToUpdate(account);
      const apiData = await accountsApi.updateAccount(accountId, updateData);
      return mapAccountToUI(apiData);
    },
    onMutate: async ({ accountId }) => {
      await queryClient.cancelQueries({ queryKey: accountsKeys.detail(accountId) });

      const previousAccountData = queryClient.getQueryData<AccountOutputSchema>(
        accountsKeys.detail(accountId)
      );

      return { previousAccountData };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousAccountData) {
        queryClient.setQueryData(
          accountsKeys.detail(_variables.accountId),
          context.previousAccountData
        );
      }
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({ queryKey: accountsKeys.detail(variables.accountId) });
      queryClient.invalidateQueries({ queryKey: accountsKeys.lists() });
    },
  });
}
